(() => {
  'use strict';

  const STORAGE_KEY = 'velvele:card-preview-mode';
  const CHANGE_EVENT = 'vc:global-media-mode-change';
  const SCOPE_SELECTOR = '[data-vc-global-media-scope]';
  const SWITCHABLE_SELECTOR = 'vc-card-media[data-vc-switchable="true"]';

  const readMode = () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved === 'imagery' || saved === 'null') return saved;
    } catch (_) {}
    return 'imagery';
  };

  const state = {
    mode: readMode(),
    get role() {
      return this.mode === 'null' ? 'alternate' : 'primary';
    },
    applyToScope(scope, notify = false, userInitiated = false) {
      if (!(scope instanceof Element) || !scope.matches(SCOPE_SELECTOR)) return;
      const nextRole = this.role;
      const changed = scope.dataset.vcCover !== nextRole;
      scope.dataset.vcCover = nextRole;
      if (notify && changed) {
        scope.dispatchEvent(new CustomEvent('vc:cover-change', {
          bubbles: false,
          detail: { role: nextRole, userInitiated }
        }));
      }
    },
    setMode(nextMode, userInitiated = false) {
      const safeMode = nextMode === 'null' ? 'null' : 'imagery';
      this.mode = safeMode;
      try { sessionStorage.setItem(STORAGE_KEY, safeMode); } catch (_) {}
      document.querySelectorAll(SCOPE_SELECTOR).forEach((scope) => {
        this.applyToScope(scope, true, userInitiated);
      });
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT, {
        detail: { mode: safeMode, role: this.role, userInitiated }
      }));
    }
  };

  window.VelveleCardPreviewMode = state;

  class VcGlobalMediaMode extends HTMLElement {
    constructor() {
      super();
      this.observedCards = new WeakSet();
      this.visibleCards = new Set();
      this.hideTimer = 0;
      this.frame = 0;
      this.cardObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0) this.visibleCards.add(entry.target);
          else this.visibleCards.delete(entry.target);
        });
        this.scheduleVisibility();
      }, { threshold: [0, 0.01], rootMargin: '0px 0px -4% 0px' });
      this.onStateChange = () => this.renderMode();
      this.onCapability = (event) => this.observeCard(event.target);
      this.onClick = (event) => {
        const button = event.target.closest('button[data-vc-global-mode]');
        if (!button || !this.contains(button)) return;
        const nextMode = button.dataset.vcGlobalMode === 'null' ? 'null' : 'imagery';
        if (nextMode === state.mode) return;
        this.playTactileFeedback();
        state.setMode(nextMode, true);
      };
      this.onViewportChange = () => this.scheduleVisibility();
    }

    connectedCallback() {
      if (this.dataset.vcInitialized === 'true') return;
      this.dataset.vcInitialized = 'true';
      this.control = this.querySelector('.vc-global-media-mode__control');
      this.control?.classList.add('vc-tactile-target');
      this.addEventListener('click', this.onClick);
      window.addEventListener(CHANGE_EVENT, this.onStateChange);
      document.addEventListener('vc:card-media:capability', this.onCapability);
      window.addEventListener('resize', this.onViewportChange, { passive: true });
      window.visualViewport?.addEventListener('resize', this.onViewportChange, { passive: true });
      window.visualViewport?.addEventListener('scroll', this.onViewportChange, { passive: true });

      this.mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => this.registerTree(node));
          } else if (mutation.target instanceof Element && mutation.target.matches(SWITCHABLE_SELECTOR)) {
            this.observeCard(mutation.target);
          }
        });
        this.scheduleVisibility();
      });
      this.mutationObserver.observe(document.documentElement, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'open', 'aria-hidden', 'aria-expanded', 'data-vc-switchable']
      });

      document.querySelectorAll(SCOPE_SELECTOR).forEach((scope) => state.applyToScope(scope));
      document.querySelectorAll(SWITCHABLE_SELECTOR).forEach((card) => this.observeCard(card));
      this.renderMode();
      this.scheduleVisibility();
    }

    disconnectedCallback() {
      clearTimeout(this.hideTimer);
      window.VelveleTactileFeedback?.clear(this.control);
      cancelAnimationFrame(this.frame);
      this.cardObserver.disconnect();
      this.mutationObserver?.disconnect();
      this.removeEventListener('click', this.onClick);
      window.removeEventListener(CHANGE_EVENT, this.onStateChange);
      document.removeEventListener('vc:card-media:capability', this.onCapability);
      window.removeEventListener('resize', this.onViewportChange);
      window.visualViewport?.removeEventListener('resize', this.onViewportChange);
      window.visualViewport?.removeEventListener('scroll', this.onViewportChange);
    }

    playTactileFeedback() {
      window.VelveleTactileFeedback?.play(this.control);
    }

    registerTree(node) {
      if (!(node instanceof Element)) return;
      if (node.matches(SCOPE_SELECTOR)) state.applyToScope(node);
      node.querySelectorAll?.(SCOPE_SELECTOR).forEach((scope) => state.applyToScope(scope));
      if (node.matches(SWITCHABLE_SELECTOR)) this.observeCard(node);
      node.querySelectorAll?.(SWITCHABLE_SELECTOR).forEach((card) => this.observeCard(card));
    }

    observeCard(card) {
      if (!(card instanceof Element) || card.dataset.vcSwitchable !== 'true' || this.observedCards.has(card)) return;
      const scope = card.closest(SCOPE_SELECTOR);
      if (!scope) return;
      state.applyToScope(scope);
      this.observedCards.add(card);
      this.cardObserver.observe(card);
    }

    renderMode() {
      this.dataset.mode = state.mode;
      this.querySelectorAll('button[data-vc-global-mode]').forEach((button) => {
        const active = button.dataset.vcGlobalMode === state.mode;
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    scheduleVisibility() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.updateVisibility();
      });
    }

    isRendered(element) {
      if (!element?.isConnected || element.getClientRects().length === 0) return false;
      const style = getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.visualViewport?.height || innerHeight;
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < viewportHeight;
    }

    hasBlockingOverlay() {
      if (document.hidden || document.body.classList.contains('vc-nav-lock') ||
          document.body.classList.contains('overflow-hidden-drawer')) return true;
      const selectors = [
        '.vc-nav-open',
        'cart-drawer.active',
        'dialog[open]',
        '[role="dialog"][aria-modal="true"]',
        '#shopify-pc__banner',
        '.shopify-pc__banner__dialog'
      ];
      return selectors.some((selector) => Array.from(document.querySelectorAll(selector)).some((element) => {
        if (element === this || element.closest('details:not([open])')) return false;
        return this.isRendered(element) && element.getAttribute('aria-hidden') !== 'true';
      }));
    }

    updateVisibility() {
      for (const card of this.visibleCards) {
        if (!card.isConnected || card.dataset.vcSwitchable !== 'true') this.visibleCards.delete(card);
      }
      if (this.hasBlockingOverlay()) {
        clearTimeout(this.hideTimer);
        this.hideTimer = 0;
        this.setVisible(false);
        return;
      }
      const shouldShow = Array.from(this.visibleCards).some((card) => this.isRendered(card));

      if (shouldShow) {
        clearTimeout(this.hideTimer);
        this.hideTimer = 0;
        this.setVisible(true);
      } else if (this.dataset.visible === 'true' && !this.hideTimer) {
        this.hideTimer = setTimeout(() => {
          this.hideTimer = 0;
          if (this.hasBlockingOverlay() || !Array.from(this.visibleCards).some((card) => this.isRendered(card))) {
            this.setVisible(false);
          }
        }, 180);
      } else if (this.dataset.visible !== 'true') {
        this.setVisible(false);
      }
    }

    setVisible(visible) {
      const next = visible ? 'true' : 'false';
      if (this.dataset.visible === next) return;
      this.dataset.visible = next;
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');
      this.inert = !visible;
      document.documentElement.classList.toggle('vc-global-mode-visible', visible);
    }
  }

  if (!customElements.get('vc-global-media-mode')) {
    customElements.define('vc-global-media-mode', VcGlobalMediaMode);
  }
})();
